import React, { useCallback, useEffect, useState } from 'react';
import { FaInfoCircle } from 'react-icons/fa';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { FinTextarea } from './FinField';
import '../css/financeiro-parcela-pagar.css';

// O mesmo azul do painel — sem a pílula, é a cor que diz que aquilo é informação e é clicável.
const AZUL = '#2d7ff9';

// O popover da observação, o mesmo para ler e para escrever: título, largura e rolagem. `pre-wrap`
// e rolagem porque é aqui que o texto aparece inteiro, com as quebras que o autor escreveu — o
// `title` do botão só dá conta de uma linha. `className` leva o `.fin-pp` à escrita: o popover vai
// para o `body`, fora do escopo dos tokens, e o campo e os botões precisam deles.
const popoverObservacao = (rotulo, conteudo, className) => (
    <Popover id="observacao" className={className} style={{ width: 300, maxWidth: 'calc(100vw - 32px)' }}>
        <Popover.Title as="h3">{rotulo}</Popover.Title>
        <Popover.Content style={{ whiteSpace: 'pre-wrap', fontSize: 13, color: '#0f172a', maxHeight: 260, overflowY: 'auto' }}>
            {conteudo}
        </Popover.Content>
    </Popover>
);

// Um popover de observação aberto por vez, na tela inteira. O gatilho para a propagação do clique
// (para não recolher a barra em volta), e por isso o `rootClose` do que está aberto nunca fica
// sabendo do clique no gatilho de outro: os dois ficavam abertos. Quem abre fecha o anterior.
let fecharOAberto = null;
function usePopoverUnico() {
    const [aberto, setAberto] = useState(false);
    const fechar = useCallback(() => setAberto(false), []);
    const alternar = (abrir) => {
        if (abrir) {
            if (fecharOAberto && fecharOAberto !== fechar) fecharOAberto();
            fecharOAberto = fechar;
        } else if (fecharOAberto === fechar) {
            fecharOAberto = null;
        }
        setAberto(abrir);
    };
    // Saiu da tela aberto (filtro, recarga): não deixa o fechar dele pendurado.
    useEffect(() => () => { if (fecharOAberto === fechar) fecharOAberto = null; }, [fechar]);
    return [aberto, alternar];
}

/**
 * Observação em badge: o (i) abre o texto inteiro num popover, e o hover mostra ele numa linha só.
 *
 * É UM componente para todos os lugares que mostram observação — o cabeçalho da solicitação no
 * painel, o cabeçalho do trecho, a coluna do board e o rótulo do campo no formulário. Havia quatro
 * versões quase iguais, cada uma com o seu azul e o seu arredondamento, e só uma delas abria o
 * popover; as outras deixavam o texto preso no `title`, ilegível quando tinha quebra de linha.
 *
 * O clique PARA no invólucro: estes badges vivem dentro de barras que abrem e fecham ao clicar, e
 * abrir a observação não pode recolher o que está embaixo.
 *
 * O popover vai no `body`: recolhido, o cartão é baixo e tem `overflow: hidden`, que o cortaria.
 *
 * @param {string}  texto     a observação; vazia, nada é renderizado
 * @param {boolean} compacta  só o (i), sem o texto — para coluna estreita e cabeçalho recolhido
 * @param {boolean} semBadge  o (i) cru, sem a pílula em volta — para quando ele entra numa linha
 *                            que já tem moldura própria, como o rótulo de um campo
 * @param {string}  rotulo    o que isto se chama, no hover e no título do popover
 * @param {boolean} comoTexto o texto corrido numa linha, sem badge nem (i), para célula de tabela:
 *                            o hover na linha da tabela sublinha (`.fin-pp__obs-celula`) e o
 *                            clique abre o mesmo popover
 */
export default function PillObservacao({ texto, compacta = false, semBadge = false,
    rotulo = 'Observação do trecho', comoTexto = false }) {
    const umaLinha = String(texto ?? '').replace(/\s+/g, ' ').trim();
    const [aberto, alternar] = usePopoverUnico();
    if (!umaLinha) return null;
    if (comoTexto) {
        return (
            <span onClick={e => e.stopPropagation()} style={{ display: 'block', minWidth: 0 }}>
                <OverlayTrigger trigger="click" placement="bottom-start" rootClose container={document.body}
                    show={aberto} onToggle={alternar} overlay={popoverObservacao(rotulo, texto)}>
                    <button type="button" title={`${rotulo}: ${umaLinha}`}
                        className="btn btn-sm p-0 border-0 bg-transparent text-start text-truncate fin-pp__obs-linha fin-pp__obs-celula"
                        style={{ display: 'block', maxWidth: 260, font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
                        {umaLinha}
                    </button>
                </OverlayTrigger>
            </span>
        );
    }
    return (
        <span onClick={e => e.stopPropagation()} style={{ display: 'inline-flex', minWidth: 0, flexShrink: compacta ? 0 : 100000 }}>
            <OverlayTrigger trigger="click" placement="bottom-start" rootClose container={document.body}
                show={aberto} onToggle={alternar} overlay={popoverObservacao(rotulo, texto)}>
                <button type="button" title={`${rotulo}: ${umaLinha}`}
                    className={semBadge
                        ? 'btn btn-sm p-0 border-0 bg-transparent d-inline-flex align-items-center'
                        : 'fin-pp__minitag is-info'}
                    style={semBadge ? { color: AZUL } : {
                        borderRadius: 999, fontSize: 11, gap: 4, fontFamily: 'inherit', cursor: 'pointer',
                        minWidth: 26, maxWidth: compacta ? undefined : 280,
                    }}>
                    <FaInfoCircle size={semBadge || compacta ? 13 : 11} style={{ flexShrink: 0 }} />
                    {!compacta && !semBadge && <span className="text-truncate">{umaLinha}</span>}
                </button>
            </OverlayTrigger>
        </span>
    );
}

/**
 * Um ícone que abre o MESMO popover da observação com um texto curto, para marca que não cabe na
 * célula: a conferência do DBL CHECK na tela do RSVP (a data e quem conferiu). Sem moldura de botão —
 * o ícone já diz que abre.
 *
 * @param {string} rotulo   título do popover e começo do hover
 * @param {string} texto    o que o popover mostra
 * @param {string} cor      a cor do ícone
 * @param {node}   children o ícone
 */
export function IconeInfo({ rotulo, texto, cor, children }) {
    const [aberto, alternar] = usePopoverUnico();
    return (
        <span onClick={e => e.stopPropagation()} style={{ display: 'inline-flex' }}>
            <OverlayTrigger trigger="click" placement="bottom-start" rootClose container={document.body}
                show={aberto} onToggle={alternar} overlay={popoverObservacao(rotulo, texto)}>
                <button type="button" title={`${rotulo}: ${texto}`} aria-label={`${rotulo}: ${texto}`}
                    className="btn btn-sm p-0 border-0 bg-transparent d-inline-flex align-items-center"
                    style={{ color: cor, cursor: 'pointer', lineHeight: 1 }}>
                    {children}
                </button>
            </OverlayTrigger>
        </span>
    );
}

/**
 * O MESMO popover da observação, aberto pelo que vier em `children` e com o que vier em `conteudo`: a
 * célula de observação da planilha de emissão mostra a anotação mais nova de cada lado numa linha, e o
 * clique abre todas, inteiras, com quem e quando.
 *
 * @param {string} rotulo   título do popover
 * @param {node}   conteudo o que o popover mostra
 * @param {string} titulo   o hover do gatilho
 * @param {node}   children o gatilho: o que a célula mostra
 */
export function PopoverObservacao({ rotulo, conteudo, titulo, children }) {
    const [aberto, alternar] = usePopoverUnico();
    return (
        <span onClick={e => e.stopPropagation()} style={{ display: 'block', minWidth: 0 }}>
            <OverlayTrigger trigger="click" placement="bottom-start" rootClose container={document.body}
                show={aberto} onToggle={alternar} overlay={popoverObservacao(rotulo, conteudo)}>
                <button type="button" title={titulo}
                    className="btn btn-sm p-0 border-0 bg-transparent text-start"
                    style={{ display: 'block', width: '100%', maxWidth: 260, font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
                    {children}
                </button>
            </OverlayTrigger>
        </span>
    );
}

/**
 * A mesma observação, para escrever: o botão (`children`) abre o MESMO popover da leitura, com o
 * texto num campo e Salvar. Quem chama decide o que o Salvar faz — na planilha de emissão, o
 * rascunho da linha, que só vai no envio. Cancelar, ou clicar fora, descarta o que foi digitado.
 *
 * @param {string}   texto        o que já está escrito (abre no campo)
 * @param {string}   rotulo       título do popover
 * @param {string}   placeholder
 * @param {Function} onSalvar     recebe o texto do campo
 * @param {node}     children     o botão que abre; um elemento só, que aceita ref
 */
export function ObservacaoEditavel({ texto, rotulo, placeholder, onSalvar, children }) {
    const [aberto, alternarUnico] = usePopoverUnico();
    const [rascunho, setRascunho] = useState('');
    // Toda abertura começa do que está escrito: o que ficou do Cancelar não volta.
    const alternar = (abrir) => {
        if (abrir) setRascunho(texto ?? '');
        alternarUnico(abrir);
    };
    return (
        <span onClick={e => e.stopPropagation()} style={{ display: 'inline-block' }}>
            {/* Foco no campo só depois de o popover estar no lugar, e sem rolar: com `autoFocus` ele
                recebia o foco ainda no topo da página, antes de o Popper posicionar, e a página subia. */}
            <OverlayTrigger trigger="click" placement="bottom-start" rootClose container={document.body}
                show={aberto} onToggle={alternar}
                onEntered={no => no?.querySelector('textarea')?.focus({ preventScroll: true })}
                overlay={popoverObservacao(rotulo, (
                    <>
                        <FinTextarea rows={5} value={rascunho} placeholder={placeholder}
                            onChange={e => setRascunho(e.target.value)} style={{ whiteSpace: 'normal', fontSize: 13 }} />
                        <div className="d-flex justify-content-end gap-2" style={{ marginTop: 8, whiteSpace: 'normal' }}>
                            <button type="button" className="fin-pp__btn fin-pp__btn--sm" onClick={() => alternar(false)}>Cancelar</button>
                            <button type="button" className="fin-pp__btn fin-pp__btn--sm fin-pp__btn--primary"
                                onClick={() => { onSalvar(rascunho); alternar(false); }}>Salvar</button>
                        </div>
                    </>
                ), 'fin-pp')}>
                {children}
            </OverlayTrigger>
        </span>
    );
}
